import { router } from "@inertiajs/react"
import { Check, Star } from "lucide-react"

import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import { Button } from "@/components/ui/button"
import {
  settingsIntegrationConnectPath,
  settingsIntegrationDisconnectPath,
} from "@/routes"

import { useStepSave } from "./nav"
import type { ReviewResponderStepProps } from "./types"

const GBP_KIND = "google_business_profile"

// Step 1 — Connect Google Business Profile. The actual OAuth connect/disconnect
// flow is implemented in IMP-303; this step renders the connection state and
// gates advancing until a profile is connected (the agent reads and replies to
// reviews on it, so nothing downstream can be configured without it).
export function ConnectStep({
  agent,
  stepNumber,
  totalSteps,
  gbpConnection,
}: ReviewResponderStepProps) {
  const { processing, save } = useStepSave(agent.uid)

  function connect() {
    // Full-page navigation — the OAuth handshake redirects off-site and the
    // callback brings the user back here via return_to (IMP-303).
    const returnTo = encodeURIComponent(`/agents/${agent.uid}`)
    window.location.href = `${settingsIntegrationConnectPath(GBP_KIND)}?return_to=${returnTo}`
  }

  function disconnect() {
    router.delete(settingsIntegrationDisconnectPath(GBP_KIND), {
      preserveScroll: true,
    })
  }

  const locationLabel =
    gbpConnection.location_count === 1
      ? "1 location"
      : `${gbpConnection.location_count ?? 0} locations`

  return (
    <WizardLayout
      footer={
        <Button
          onClick={() => save("step-tone", {})}
          disabled={processing || !gbpConnection.connected}
        >
          Continue
        </Button>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Connect Google Business Profile"
        description="The agent reads and replies to reviews on your Google Business Profile. Connect it to continue."
      />

      <div className="rounded-lg border p-4">
        <div className="flex items-start gap-3">
          <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg">
            <Star className="size-5 text-amber-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Google Business Profile</p>
            {gbpConnection.connected ? (
              <p className="text-muted-foreground mt-0.5 text-sm">
                {gbpConnection.business_name}
                {gbpConnection.business_name ? ", " : ""}
                {locationLabel}
              </p>
            ) : (
              <p className="text-muted-foreground mt-0.5 text-sm">
                Not connected
              </p>
            )}
          </div>
          {gbpConnection.connected ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border px-2 py-1 text-xs font-medium">
              <Check className="size-3.5 text-green-600" />
              Connected
            </span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={connect}
            >
              Connect
            </Button>
          )}
        </div>

        {gbpConnection.connected && (
          <div className="mt-3 flex justify-end border-t pt-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              onClick={disconnect}
            >
              Disconnect
            </Button>
          </div>
        )}
      </div>
    </WizardLayout>
  )
}
